"use client";
import { Link } from "@/i18n/navigation";
import { formatCurrency } from "@/utils/currencyFormatter";
import {
  dateFormats,
  formatDate,
  getShortMonthName,
} from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLocale } from "next-intl";
import Image from "next/image";
import { memo, useMemo } from "react";
import { useSearchParams } from "next/navigation";

const DatePickerFlexibleContent = memo(
  ({
    cheapestFlightsMonthly,
    cheapestFlightsMonthlyByMonth,
    getCheapestFlightsMonthlyByMonth,
    currency,
    isRoundTrip,
    translationKey,
  }) => {
    const locale = useLocale();
    const t = useTranslationDetector(translationKey);
    const searchParams = useSearchParams();

    const passengerCounts = useMemo(() => {
      if (typeof window === "undefined" || !searchParams) {
        return { adult: 1, child: 0, infant: 0, student: 0, senior: 0 };
      }
      const senior = parseInt(searchParams.get("senior") || "0", 10);
      const elderly = parseInt(searchParams.get("elderly") || "0", 10);
      return {
        adult: parseInt(searchParams.get("adult") || "1", 10),
        student: parseInt(searchParams.get("student") || "0", 10),
        child: parseInt(searchParams.get("child") || "0", 10),
        infant: parseInt(searchParams.get("infant") || "0", 10),
        senior: senior + elderly, // elderly değeri senior'a eklenir
      };
    }, [searchParams]);

    const totalPassengerCount = useMemo(() => {
      if (passengerCounts == null) {
        return 0;
      }
      if (
        typeof passengerCounts === "number" ||
        typeof passengerCounts === "string"
      ) {
        return passengerCounts;
      }
      if (
        typeof passengerCounts === "object" &&
        !Array.isArray(passengerCounts)
      ) {
        return Object.values(passengerCounts).reduce((sum, val) => {
          const n =
            typeof val === "number" ? val : Number.parseInt(String(val), 10);
          return sum + (Number.isNaN(n) ? 0 : n);
        }, 0);
      }
      return 0;
    }, [passengerCounts]);

    return (
      <div className="flex h-full min-h-0 flex-1 flex-col pt-4 md:h-auto">
        <div className="flex flex-col gap-4 bg-white pt-4 md:pt-0">
          <div className="flex flex-col gap-1 pl-5">
            <span className="text-sm font-semibold">{t("monthSelection")}</span>
            <span className="text-foreground-muted text-xs font-medium">
              {t("estimatedPricesDescription", { 0: totalPassengerCount })}
            </span>
          </div>
          <div className="scrollbar-hide flex gap-3 overflow-x-auto">
            {cheapestFlightsMonthly?.MonthlyFlightFares.map((item, index) => (
              <button
                key={index}
                onClick={() =>
                  getCheapestFlightsMonthlyByMonth(item.Month, item.Year, index)
                }
                className={clsx(
                  "relative flex h-[140px] min-w-[100px] flex-col items-center justify-center overflow-hidden pb-4",
                  index ===
                    cheapestFlightsMonthly?.MonthlyFlightFares.length - 1
                    ? "mr-4"
                    : "",
                  index === 0 ? "ml-4" : "",
                )}
              >
                <div
                  className={clsx(
                    "relative flex h-full w-full cursor-pointer flex-col items-center justify-center gap-[10px] overflow-hidden rounded-lg",
                    item.selected
                      ? "border-primary bg-primary-weaker border-2"
                      : "border-border border",
                  )}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-xs">{item.Year}</span>
                    <span className="font-medium">
                      {getShortMonthName(item.Month - 1, locale)}
                    </span>
                    <span className="text-xs">
                      {formatCurrency(item.AverageNetAmount, currency)}
                    </span>
                  </div>
                  {item.Month ==
                    cheapestFlightsMonthly?.MinYearlyFlightFare.Month && (
                    <div className="bg-teal-weak text-teal absolute bottom-0 left-0 h-[22px] w-full text-xs">
                      {t("cheapest")}
                    </div>
                  )}
                </div>

                {item.selected && (
                  <span className="bg-primary-weaker absolute top-full left-1/2 -mt-2 h-[22px] w-[22px] -translate-x-1/2 rotate-45 transform text-white"></span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-background-global flex h-full flex-col gap-4 py-4 md:h-auto">
          {cheapestFlightsMonthlyByMonth ? (
            <>
              <div className="flex flex-col gap-1 pl-5">
                <span className="text-sm font-semibold">
                  {t("cheapestFlights")}
                </span>
                <span className="text-foreground-muted text-xs font-medium">
                  {t("cheapestFlightsDescription")}
                </span>
              </div>
              <div className="scrollbar-hide gap-4 space-y-4 overflow-y-auto px-4 md:flex md:flex-row md:space-y-0 md:overflow-x-auto md:px-0">
                {cheapestFlightsMonthlyByMonth?.map((item, index) => (
                  <Link
                    key={index}
                    className={clsx(
                      "flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm md:w-[260px] md:min-w-[260px]",
                      index === cheapestFlightsMonthlyByMonth.length - 1
                        ? "md:mr-4"
                        : "",
                      index === 0 ? "md:ml-4" : "",
                    )}
                    href={`/flight/${isRoundTrip ? "round-trip" : "one-way"}/${item.Origin.AirportSlugToSearch.id}-${item.Destination.AirportSlugToSearch.id}/${formatDate(item.OutboundDepartureDate, { format: "YYYY-MM-DD" })}${isRoundTrip ? `/${formatDate(item.OutboundArrivalDate, { format: "YYYY-MM-DD" })}` : ""}`}
                  >
                    <div
                      className={clsx(
                        "flex w-full items-center justify-between px-4 pt-4",
                        isRoundTrip ? "pb-0" : "pb-4",
                      )}
                    >
                      <div className="flex h-full flex-col md:h-auto">
                        {isRoundTrip && (
                          <span className="text-foreground-muted text-xs">
                            {t("departure")}
                          </span>
                        )}
                        <span className="font-semibold">
                          {dateFormats.dayLongMonthShortDay(
                            item.OutboundDepartureDate,
                            locale,
                          )}
                        </span>
                        <div className="text-foreground-muted flex items-center gap-2">
                          <span className="text-sm">
                            {item.Origin.AirportCode}
                          </span>
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["half-right-arrow"]}
                          />
                          <span className="text-sm">
                            {item.Destination.AirportCode}
                          </span>
                        </div>
                      </div>
                      {!isRoundTrip && (
                        <div className="flex flex-col items-end">
                          <span className="text-foreground-muted text-xs">
                            {t("basePrice")}
                          </span>
                          <span className="text-lg font-semibold">
                            {formatCurrency(item.AdultBaseFare, currency)}
                          </span>
                        </div>
                      )}
                    </div>
                    {isRoundTrip && (
                      <div className="border-primary-weak mt-3 flex w-full border-t px-4 pt-3 pb-0">
                        <div className="flex flex-col">
                          <span className="text-foreground-muted text-xs">
                            {t("return")}
                          </span>
                          <span className="font-semibold">
                            {dateFormats.dayLongMonthShortDay(
                              item.OutboundDepartureDate,
                              locale,
                            )}
                          </span>
                          <div className="text-foreground-muted flex items-center gap-2">
                            <span className="text-sm">
                              {item.Destination.AirportCode}
                            </span>
                            <FontAwesomeIcon
                              icon={byPrefixAndName.fak["half-right-arrow"]}
                            />
                            <span className="text-sm">
                              {item.Origin.AirportCode}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {isRoundTrip && (
                      <div className="flex w-full flex-col items-end px-4 py-3">
                        <span className="text-foreground-muted text-xs">
                          {t("basePrice")}
                        </span>
                        <span className="text-lg font-semibold">
                          {formatCurrency(item.AdultBaseFare, currency)}
                        </span>
                      </div>
                    )}

                    <span className="text-primary border-primary-weak flex w-full items-center justify-center gap-2 border-t pt-3 pb-4 text-center font-medium">
                      <span>{t("listSeeAll")}</span>
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["arrow-right"]}
                      />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div>
              <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
                <Image
                  src="/images/svg/telescope.svg"
                  alt="asd"
                  width={60}
                  height={60}
                />
                <div className="flex flex-col gap-2">
                  <span className="text-center text-lg font-semibold">
                    {t("noResultsFound")}
                  </span>
                  <span className="text-center text-sm">
                    {t("changeSearchCriteria")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

DatePickerFlexibleContent.displayName = "DatePickerFlexibleContent";

export default DatePickerFlexibleContent;
